import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import TopNavbar from '../home/TopNavbar';
import TopHeader from '../home/TopHeader';
import AllQuestions from '../AllData/AllQuestions';
import Loading from '../home/Loading';
import Latex from 'react-latex';
import './../../pagescss/QuizDetailsPage.css';
import QuestionImage from './QuestionImage';


// Helper Functions
const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours < 10 ? "0" : ""}${hours}:${minutes < 10 ? "0" : ""}${minutes}:${secs < 10 ? "0" : ""}${secs}`;
};

const parseAnswers = (answers) => {
  if (Array.isArray(answers)) return answers;
  if (typeof answers === 'string') {
    try {
      const parsed = JSON.parse(answers);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing answers JSON:', error);
      return [];
    }
  }
  return [];
};

const calculateSubmissionStats = (answers) => {
  const parsedAnswers = parseAnswers(answers);
  let totalMarks = 0;
  let totalCorrectAns = 0;
  let totalIncorrectAns = 0;
  let totalNotAns = 0;

  parsedAnswers.forEach((ans) => {
    if (ans.userAnswer === ans.correctAnswer) {
      totalMarks += 4;
      totalCorrectAns++;
    } else if (["option1", "option2", "option3", "option4"].includes(ans.userAnswer)) {
      totalMarks -= 1;
      totalIncorrectAns++;
    } else {
      totalNotAns++;
    }
  });

  return {
    totalMarks,
    totalCorrectAns,
    totalIncorrectAns,
    totalNotAns,
    percentage: parsedAnswers.length > 0 ? Math.round((totalMarks * 100) / (parsedAnswers.length * 4)) : 0
  };
};

function QuizHandler() {
  const { type, subject, topic, quizNo, subjectName } = useParams();
  const quizId = `${type}${subject}${topic}${quizNo}`;

  // Selection Page States
  const [quesNum, setQuesNum] = useState(subject === "atoz" ? 40 : topic === "atoz" ? 30 : 10);

  // Quiz Details States
  const [submissions, setSubmissions] = useState([]);
  const [quizSubmissions, setQuizSubmissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuizName, setSelectedQuizName] = useState('');
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);

  const isPayment = localStorage.getItem('userPayment');


  const questions = AllQuestions;

  const [showContent, setShowContent] = useState(false);
  const handleToggle = () => {
    setShowContent(!showContent);
  };

  // Determine min and max questions
  let minQues = 10, maxQues = 10;
  if (subject === "atoz") {
    minQues = 40; maxQues = 180;
  } else if (topic === "atoz") {
    minQues = 10; maxQues = 50;
  }

  // Load submissions and bookmarks from localStorage
  useEffect(() => {
    const savedSubmissions = localStorage.getItem('quizSubmissions');
    const subData = savedSubmissions ? JSON.parse(savedSubmissions) : [];
    setSubmissions(subData);

    const storedBookmarks = localStorage.getItem('questionBookmarks');
    if (storedBookmarks) {
      setBookmarkedQuestions(JSON.parse(storedBookmarks));
    }
  }, []);

  // Filter submission for the specific quizId
  useEffect(() => {
    if (quizId && submissions.length > 0) {
      const filtered = submissions.filter(data => data.quizId === quizId);
      const processed = filtered.map(sub => ({
        ...sub,
        answers: parseAnswers(sub.answers)
      }));
      setQuizSubmissions(processed);
    } else {
      setQuizSubmissions([]);
    }
  }, [submissions, quizId]);

  // Input change handlers
  const handleInputChange = (e) => {
    let value = parseInt(e.target.value) || minQues;
    if (value < minQues) value = minQues;
    if (value > maxQues) value = maxQues;
    setQuesNum(value);
  };

  const increment = () => setQuesNum(prev => Math.min(prev + 1, maxQues));
  const decrement = () => setQuesNum(prev => Math.max(prev - 1, minQues));

  // Modal and reset handlers
  const triggerResetModal = (quizName) => {
    setSelectedQuizName(quizName || 'this quiz');
    setShowModal(true);
  };

  const handleConfirmReset = () => {
    const updatedSubmissions = submissions.filter((item) => item.quizId !== quizId);
    localStorage.setItem('quizSubmissions', JSON.stringify(updatedSubmissions));
    setSubmissions(updatedSubmissions);
    setQuizSubmissions([]);
    setShowModal(false);
  };

  // Bookmark handlers
  const compareQuestionIds = (id1, id2) => {
    const extractId = (id) => (typeof id === 'object' && id.$oid ? id.$oid : id);
    return extractId(id1) === extractId(id2);
  };

  const handleBookmark = (questionId) => {
    const isBookmarked = bookmarkedQuestions.some(bq => compareQuestionIds(bq.questionId, questionId));
    let updatedBookmarks;
    if (isBookmarked) {
      updatedBookmarks = bookmarkedQuestions.filter(bq => !compareQuestionIds(bq.questionId, questionId));
    } else {
      updatedBookmarks = [...bookmarkedQuestions, { questionId, timestamp: new Date().toISOString() }];
    }
    localStorage.setItem('questionBookmarks', JSON.stringify(updatedBookmarks));
    setBookmarkedQuestions(updatedBookmarks);
  };

  const isQuestionBookmarked = (questionId) => {
    return bookmarkedQuestions.some(bq => compareQuestionIds(bq.questionId, questionId));
  };

  const renderWithLatex = (text) => {
    if (!text) return null;
    return (text.includes('$') || text.includes('\\')) ? <Latex>{text}</Latex> : text;
  };

  const userSubmissionExists = quizSubmissions.length > 0;

  // View 1: Show Quiz Details & Answers (If submission exists)
  if (userSubmissionExists) {
    return (
      <div className="quiz-details">
        <TopNavbar name="Mock Test Attempt Details" />
        <TopHeader name="Mock Test Attempt Details" />

        <div className="quiz-details__card">
          <ul className="submissions">
            {quizSubmissions.map((submission, submissionIndex) => {
              const stats = calculateSubmissionStats(submission.answers);
              const answersArray = submission.answers;

              return (
                <li key={submission.submissionId || submission._id || submissionIndex} className="submission">
                  <div className="submission__header">
                    <div className="submission__info">
                      <p className="submission__text"><span className="submission__label">Mock test :</span> {submission.quizName} mock test</p>
                      <p className="submission__text"><span className="submission__label">Submitted:</span> {submission.submittedAt}</p>
                      <p className="submission__text"><span className="submission__label">Time Taken:</span> {formatTime(submission.timeTaken)}</p>
                    </div>
                    <div className="submission__info">
                      <p className="submission__text"><span className="submission__label">Score:</span> {stats.totalMarks}/{answersArray.length * 4} ({stats.percentage}%)</p>
                      <p className="submission__text"><span className="submission__label">Correct:</span> {stats.totalCorrectAns}</p>
                      <p className="submission__text"><span className="submission__label">Incorrect:</span> {stats.totalIncorrectAns}</p>
                      <p className="submission__text"><span className="submission__label">Not Answered:</span> {stats.totalNotAns}</p>
                    </div>
                  </div>

                  <div className="submission__actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: '10px' }}>
                    <button onClick={() => triggerResetModal(submission.quizName)} className="reset-test-btn" style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                      Reset Test
                    </button>
                    <Link to={`/quiz-restarting/${quizId}`} className="remock-test-link" style={{ backgroundColor: '#ffe54f', color: '#1b1717', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'none' }}>
                      Retest
                    </Link>
                  </div>

                  <h3 className="submission__questions-title">Questions and Answers</h3>

                  <ul className="questions">
                    {answersArray.map((answer, ansIndex) => {
                      if (!answer) return null;
                      const questionId = answer.questionId;
                      const questionData = questions.find(q => (q._id?.$oid || q._id) === questionId);
                      const isData = !!questionData;
                      const photo = isData ? questionData.question_img : null;

                      let correctAnswer = isData ? (questionData[answer.correctAnswer] || answer.correctAnswer) : "Not answered";
                      let userAnswer = isData ? (questionData[answer.userAnswer] || answer.userAnswer) : "Not answered";

                      return (
                        <li key={`${submission.submissionId}-${ansIndex}`} className={`question ${answer.isCorrect ? 'question--correct' : 'question--incorrect'}`}>
                          <button onClick={() => handleBookmark(answer.questionId)} className={`bookmark-button ${isQuestionBookmarked(answer.questionId) ? 'bookmarked' : ''}`}>
                            <span>{isQuestionBookmarked(answer.questionId) ? 'Marked' : 'Mark'}</span>
                          </button>

                          {questionData.type === "dpp" ? (<>
                            {questionData.question ? (
                              <p className="question-text">
                                {renderWithLatex(answer.questionText)}
                              </p>
                            ) : (
                              <div className="question__image-container">
                                <QuestionImage
                                  src={`/questionsPhoto/${questionData.question_img}`}
                                  alt="Question"
                                />
                              </div>
                            )}

                            <button onClick={handleToggle} style={{ fontSize: "8px", padding: "01px" }}>
                              {showContent ? "Hide source image" : "View source image"}
                            </button>
                            {showContent && (
                              <div style={{ padding: "5px" }}>
                                <div className="question__image-container">
                                  <QuestionImage
                                    src={`/questionsPhoto/${questionData.question_img}`}
                                    alt="Question"
                                  />
                                </div>
                              </div>
                            )}

                          </>) : (<>
                            {photo ? (
                              <div className="question__image-container">
                                <QuestionImage
                                  src={`/questionsPhoto/${questionData.question_img}`}
                                  alt="Question"
                                />
                              </div>
                            ) : (
                              <>{renderWithLatex(answer.questionText)}</>
                            )}
                          </>)}



                          <div className="question__answers">
                            <div className="answer">
                              <p className="answer__label">Your Answer:</p>
                              <p className={`answer__text answer__text--user ${answer.isCorrect ? 'answer__text--correct' : 'answer__text--incorrect'}`}>
                                {renderWithLatex(userAnswer)}
                              </p>
                            </div>
                            <div className="answer">
                              <p className="answer__label">Correct Answer:</p>
                              <p className="answer__text answer__text--correct">
                                {renderWithLatex(correctAnswer)}
                              </p>
                              <Link to={`/question-solution/${questionId}`} className="question__solution-link">View Solution</Link>
                            </div>
                          </div>
                          <br />

                          <p className={`question__status ${answer.isCorrect ? 'question__status--correct' : 'question__status--incorrect'}`}>
                            {answer.isCorrect ? "✓ Correct" : "✗ Incorrect"}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>

        {/* POPUP MODAL */}
        {showModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px 28px', maxWidth: '420px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', fontFamily: 'Arial, sans-serif' }}>
              <h2 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '700', color: '#000' }}>Confirm Reset</h2>
              <p style={{ margin: '0 0 24px 0', fontSize: '15px', color: '#222', lineHeight: '1.4' }}>
                This will generate new questions and permanently delete your previous questions for this test.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => setShowModal(false)} style={{ backgroundColor: '#cddc39', color: '#000', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                {isPayment ? (<>
                  <button onClick={handleConfirmReset} style={{ backgroundColor: '#007bff', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>Reset</button>
                </>) : (<>
                  <Link to="/product-list" className="pay-button">
                    Subscribe & Unlock Now 
                  </Link>
                </>)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // View 2: Show Number of Questions Selection (If submission does not exist)
  return (
    <div style={styles.page}>
      <TopNavbar name="Select Number of Questions" />
      <div style={styles.container}>
        <TopHeader name="Select Number of Questions" />
        <div style={styles.inputContainer}>
          <label htmlFor="questionCount" style={styles.label}>
            Number of Questions ({minQues}-{maxQues}):
          </label>
          <div style={styles.inputGroup}>
            <button
              style={quesNum <= minQues ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
              onClick={decrement}
              disabled={quesNum <= minQues}
            >
              -
            </button>
            <input
              id="questionCount"
              type="number"
              min={minQues}
              max={maxQues}
              value={quesNum}
              onChange={handleInputChange}
              style={styles.input}
            />
            <button
              style={quesNum >= maxQues ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
              onClick={increment}
              disabled={quesNum >= maxQues}
            >
              +
            </button>
          </div>
        </div>

        <Link to={`/quiz-starting/${type}/${subject}/${topic}/${quizNo}/${quesNum}/${subjectName}`} style={styles.link}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <p>Start mock test of {subjectName} ➔</p>
              <p style={styles.cardSubtitle}>{quesNum} questions selected</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', backgroundColor: '#f5f7fa', padding: '1rem', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  container: {
    width: '100%', maxWidth: '600px', backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)', padding: '1rem',
  },
  inputContainer: { marginBottom: '2rem' },
  label: { display: 'block', marginBottom: '0.75rem', color: '#34495e', fontSize: '1.1rem', fontWeight: '500' },
  inputGroup: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' },
  button: {
    width: '40px', height: '40px', borderRadius: '8px', border: 'none', backgroundColor: '#3498db',
    color: 'white', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  buttonDisabled: { backgroundColor: '#bdc3c7', cursor: 'not-allowed' },
  input: {
    width: '80px', height: '40px', borderRadius: '8px', border: '2px solid #dfe6e9',
    textAlign: 'center', fontSize: '1.1rem', fontWeight: '600', color: '#2c3e50', outline: 'none',
  },
  link: { textDecoration: 'none' },
  card: {
    backgroundColor: '#7198ebff', borderRadius: '10px', padding: '.5rem', color: 'white',
    boxShadow: '0 4px 6px rgba(32, 92, 74, 0.1)', cursor: 'pointer',
  },
  cardHeader: { textAlign: 'center' },
  cardSubtitle: { color: 'green', margin: '0.5rem 0 0', fontSize: '1rem', opacity: '0.9' },
};

export default QuizHandler;